import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { PromptConfig, PromptSetApiService } from '../../shared/api.service';
import { GenAiService, PromptExecutionRequest, PromptExecutionResponse } from '@shared-api/genAi/genAi.service';
import { WorkflowService } from '@shared-api/workflow/workflow.service';
import { UploadComponent } from '@shared-features/upload/upload.component';
import { UploadedFileInformation } from '@shared-types/shared-upload';
import { FileData, WorkflowType } from '@shared-types/shared-file';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';

@Component({
    selector: 'app-prompt-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatExpansionModule,
        UploadComponent,
        TranslateModule,
        MarkdownComponent
    ],
    templateUrl: './prompt-dialog.component.html',
    styleUrl: './prompt-dialog.component.scss'
})
 
export class PromptDialogComponent implements OnInit {
    promptForm: FormGroup;
    isSubmitting = false;
    isNewPrompt = false;
    private readonly CONTRACT_DATA_TEXT = ' \n \n \n**CONTRACT DATA**:${contract_data}';

    // Testing functionality properties
    canTest = false;
    testWorkflowId: string | undefined;
    testFileList: FileData[] = [];
    isTestingPrompt = false;
    testResults: { promptResult: string; file: string; }[] = [];
    isInitializingWorkspace = false;
    workspaceInitializationError = false;
    shouldAutoRunTest = false;
    fileFormats: string[] = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        '.docx'
    ];

    constructor(
        private fb: FormBuilder,
        private apiService: PromptSetApiService,
        private genAiService: GenAiService,
        private workflowService: WorkflowService,
        private dialogRef: MatDialogRef<PromptDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private httpClient: HttpClient,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {
        this.isNewPrompt = !data?.promptIdentifier && !data?.prompt_identifier;
        // Enable testing immediately for existing prompts
        this.canTest = !this.isNewPrompt;
        
        // Concatenate all prompt content items if there are multiple
        let details = '';
        if (data?.prompt_configuration?.[0]?.prompt?.content) {
            const contentItems = data.prompt_configuration[0].prompt.content;
            details = contentItems.map((item: any) => item.prompt_content || '').join('\n\n');
        } else {
            details = data?.details || '';
        }
        
        // For new prompts, ensure contract data is appended
        if (this.isNewPrompt && !this.hasContractDataText(details)) {
            details = details.trim() 
                ? details.trim() + '\n\n' + this.CONTRACT_DATA_TEXT
                : this.CONTRACT_DATA_TEXT;
        }
        
        this.promptForm = this.fb.group({
            title: [data?.title || '', Validators.required],
            context: [data?.prompt_configuration?.[0]?.prompt?.context || data?.context || '', Validators.required],
            details: [details, Validators.required]
        });

        // Initialize test workflow for existing prompts immediately
        if (!this.isNewPrompt) {
            this.initializeTestWorkflow();
        }
    }

    ngOnInit() {    
        this.getPrompt();
        
        // Listen for changes to ensure the contract data text remains for new prompts
        if (this.isNewPrompt) {
            this.promptForm.get('details')?.valueChanges.subscribe(value => {
                if (!this.hasContractDataText(value)) {
                    this.ensureContractDataAppended();
                }
            });
        }


    }

    get hasTestFiles(): boolean {
        return this.testFileList.length > 0;
    }

    private hasContractDataText(value: string): boolean {
        return value?.includes('**CONTRACT DATA**');
    }

    private ensureContractDataAppended(): void {
        const currentValue = this.promptForm.get('details')?.value || '';
        if (!this.hasContractDataText(currentValue)) {
            const newValue = currentValue.trim() 
                ? currentValue.trim() + '\n\n' + this.CONTRACT_DATA_TEXT
                : this.CONTRACT_DATA_TEXT;
            this.promptForm.get('details')?.setValue(newValue);
        }
    }

    private initializeTestWorkflow(): void {
        this.isInitializingWorkspace = true;
        this.workspaceInitializationError = false;
        
        // Add a timeout as safety net - if nothing happens in 10 seconds, use fallback
        setTimeout(() => {
            if (this.isInitializingWorkspace && !this.testWorkflowId) {
                this.useFallbackWorkspace();
            }
        }, 10000);
        
        // First, try to get existing content collections
        this.getOrCreateContentCollection();
    }

    private getOrCreateContentCollection(): void {
        const promptIdentifier = this.data?.promptIdentifier || this.data?.prompt_identifier;
        
        // Check if we have existing content collections
        this.getContentCollections().subscribe({
            next: (collections) => {
                // Look for a collection with the specific promptIdentifier as name
                const existingCollection = collections.find((c: any) => {
                    const nameMatch = c.name === promptIdentifier;
                    return nameMatch;
                });
                
                if (existingCollection && existingCollection.uid) {
                    this.testWorkflowId = existingCollection.uid;
                    
                    // Load existing workflow data including files and results
                    this.loadExistingWorkflowData(existingCollection.uid);
                } else {
                    // Create a new content collection
                    this.createContentCollection();
                }
            },
            error: (error) => {
                this.useFallbackWorkspace();
            }
        });
    }

    private loadExistingWorkflowData(workflowId: string): void {
        this.workflowService.getWorkflow(workflowId).subscribe({
            next: (workflowData) => {
                if (workflowData) {
                    // Load existing files
                    if (workflowData.files && workflowData.files.length > 0) {
                        this.testFileList = workflowData.files.map(file => ({
                            ...file,
                            status: file.status || 'processed' // Use existing status or mark as processed
                        }));
                    }
                    
                    // Load existing prompt results from specifications.promptResults
                    if (workflowData.specifications?.promptResults && workflowData.specifications.promptResults.length > 0) {
                        this.testResults = workflowData.specifications.promptResults.map(result => ({
                            promptResult: result.promptResult || '',
                            file: result.file || 'Unknown file'
                        }));
                    }
                }
                
                this.isInitializingWorkspace = false;
                
                // Auto-run test if requested and files are available
                if (this.shouldAutoRunTest && this.hasTestFiles) {
                    this.shouldAutoRunTest = false; // Reset the flag
                    this.runPromptTest();
                }
            },
            error: (error) => {
                // Still set the workflow ID so upload functionality works
                this.isInitializingWorkspace = false;
                this.workspaceInitializationError = false;
            }
        });
    }

    private useFallbackWorkspace(): void {
        // Use promptIdentifier as the fallback workflow name as well for consistency
        const promptIdentifier = this.data?.promptIdentifier || this.data?.prompt_identifier;
        const testWorkflowName = promptIdentifier ? `Fallback-${promptIdentifier}-${Date.now()}` : `Fallback-Test-${Date.now()}`;
        
        this.workflowService.createNewWorkflow(testWorkflowName, 'documents').subscribe({
            next: (workflow) => {
                this.testWorkflowId = workflow.uid;
                this.isInitializingWorkspace = false;
                this.workspaceInitializationError = false;
                
                // Auto-run test if requested and files are available
                if (this.shouldAutoRunTest && this.hasTestFiles) {
                    this.shouldAutoRunTest = false; // Reset the flag
                    this.runPromptTest();
                }
            },
            error: (error) => {
                this.isInitializingWorkspace = false;
                this.workspaceInitializationError = true;
                
                // Last resort: use a fixed ID that should work with upload component
                const lastResortId = 'test-workspace-' + Date.now();
                this.testWorkflowId = lastResortId;
            }
        });
    }

    private createContentCollection(): void {
        const promptIdentifier = this.data?.promptIdentifier || this.data?.prompt_identifier;
        
        if (!promptIdentifier) {
            this.useFallbackWorkspace();
            return;
        }
        
        const collectionData = {
            name: promptIdentifier,
            description: "Content collection for admin prompt execution testing"
        };

        this.createContentCollectionAPI(collectionData).subscribe({
            next: (collection) => {
                this.testWorkflowId = collection.uid;
                this.isInitializingWorkspace = false;
                this.workspaceInitializationError = false;
                
                // Auto-run test if requested and files are available
                if (this.shouldAutoRunTest && this.hasTestFiles) {
                    this.shouldAutoRunTest = false; // Reset the flag
                    this.runPromptTest();
                }
            },
            error: (error) => {
                this.isInitializingWorkspace = false;
                this.workspaceInitializationError = true;
                
                this.useFallbackWorkspace();
            }
        });
    }

    retryWorkspaceInitialization(): void {
        this.testWorkflowId = undefined;
        this.initializeTestWorkflow();
    }

    private getContentCollections(): Observable<any[]> {
        return this.workflowService.getContentCollections();
    }

    private createContentCollectionAPI(data: { name: string; description: string }): Observable<any> {
        return this.workflowService.createContentCollection(data);
    }

    onTestFileProcessed(fileInfo: UploadedFileInformation): void {
        const fileData: FileData = {
            displayName: fileInfo.displayName,
            storedName: fileInfo.storedFilename,
            status: 'processed',
            fileExtension: fileInfo.displayName.split('.').pop() || '',
            uploadedAt: new Date().toISOString(),
            uploadedBy: 'System'
        };
        this.testFileList.push(fileData);
    }

    onTestFileDeleted(fileData: FileData): void {
        this.testFileList = this.testFileList.filter(f => f.storedName !== fileData.storedName);
    }

    runPromptTest(): void {
        // Get the promptIdentifier from the current prompt
        const promptIdentifier = this.data?.promptIdentifier || this.data?.prompt_identifier;
        
        if (!promptIdentifier) {
            return;
        }

        if (!this.testFileList || this.testFileList.length === 0) {
            return;
        }

        if (!this.testWorkflowId) {
            return;
        }

        this.isTestingPrompt = true;
        
        // Force change detection to ensure UI updates
        this.cdr.detectChanges();
        
        this.testResults = [];
        
        // Add a small delay to ensure the UI has time to show the spinner
        setTimeout(() => {
            // Prepare the execution request using the existing testWorkflowId
            const request = {
                promptIdentifier: promptIdentifier,
                contentCollectionId: this.testWorkflowId!
            };
            
            // Execute the prompt
            this.genAiService.executePrompt(request).subscribe({
                next: (response) => {
                    this.testResults = response.promptResults || [];
                    this.isTestingPrompt = false;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.isTestingPrompt = false;
                    this.cdr.detectChanges();
                }
            });
        }, 100); // 100ms delay to ensure UI updates
    }

    getPrompt() {
        const promptIdentifier = this.data.promptIdentifier || this.data.prompt_identifier;
        if (promptIdentifier) {
            this.apiService.getPrompt({ promptIdentifier }).subscribe((response) => {
                
                // Patch form with data from the API response
                if (response) {
                    const promptMetadata = response.promptMetadataInformation?.[0];
                    const promptConfig = response.promptConfiguration?.[0];
                    
                    // Concatenate all prompt content items if there are multiple
                    let details = '';
                    if (promptConfig?.prompt?.content) {
                        const contentItems = promptConfig.prompt.content;
                        details = contentItems.map((item: any) => item.prompt_content || '').join('\n\n');
                    }
                    
                    this.promptForm.patchValue({
                        title: promptMetadata?.name || '',
                        context: promptConfig?.prompt?.context || '',
                        details: details
                    });

                    this.data.prompt_set_id = promptMetadata?.promptSetId;
                    this.data.createdAt = promptMetadata?.createdAt;
                    this.data.createdBy = promptConfig?.createdBy;
                    this.data.isPrivate = promptConfig?.isPrivate;
                    this.data.examples = promptConfig?.prompt?.examples || [];
                    this.data.response_format = promptConfig?.prompt?.response_format || '';
                    this.data.promptIdentifier = promptMetadata?.promptIdentifier;
                    this.data.subscription_id = promptMetadata?.subscriptionId;
                }
            });
        }
    }

    onSubmit(enableTestingMode: boolean = false) {
        if (this.promptForm.valid) {
            this.isSubmitting = true;
            const isEditMode = !!this.data?.promptIdentifier;
            // Generate prompt ID in the correct format using uuidv4
            const promptId = isEditMode ? this.data.promptIdentifier : `pid_${uuidv4().replace(/-/g, '')}`;
            const createdAt = isEditMode ? this.data.createdAt : new Date().toISOString();
            
            // Ensure details has the contract data text for new prompts
            let details = this.promptForm.value.details;
            if (!isEditMode && !this.hasContractDataText(details)) {
                details = details.trim() 
                    ? details.trim() + '\n\n' + this.CONTRACT_DATA_TEXT
                    : this.CONTRACT_DATA_TEXT;
            }
            
            const promptCreationInput: PromptConfig = {
                prompt_metadata_information: [{
                    prompt_set_id: this.data.prompt_set_id || '',
                    prompt_identifier: promptId,
                    name: this.promptForm.value.title,
                    description: this.promptForm.value.title,
                    subscription_id: this.data.subscription_id || '',
                    createdAt: createdAt
                }],
                prompt_configuration: [{
                    promptIdentifier: promptId,
                    prompt: {
                        content: [{
                            prompt_content: details
                        }],
                        context: this.promptForm.value.context,
                        examples: isEditMode ? this.data.examples || [] : [],
                        response_format: isEditMode ? this.data.response_format || "" : "",
                        use_json_mode: true
                    },
                    isPrivate: isEditMode ? (this.data.isPrivate !== undefined ? this.data.isPrivate : true) : true,
                    validationIdentifier: promptId,
                    createdBy: isEditMode ? (this.data.createdBy || "System") : "System"
                }]
            };
            
            // Use the same API service method for both add and edit
            this.apiService.storePrompt(promptCreationInput).subscribe({
                next: (response) => {
                    this.isSubmitting = false;
                    
                    // If we're enabling testing mode (Test button was clicked)
                    if (enableTestingMode) {
                        // For new prompts, enable testing functionality
                        if (this.isNewPrompt) {
                            this.canTest = true;
                            this.data.promptIdentifier = promptId;
                            this.isNewPrompt = false;
                            this.shouldAutoRunTest = true; // Flag to auto-run test after workspace initialization
                            this.initializeTestWorkflow();
                        } else {
                            // For existing prompts, run the test immediately if files are available
                            if (this.hasTestFiles && this.testWorkflowId) {
                                this.runPromptTest();
                            }
                        }
                        // Don't close the dialog, just stay open for testing
                        return;
                    }
                    
                    // Only close the dialog if Save and Close was clicked (enableTestingMode = false)
                    // Return a clean object with only the necessary data
                    const result = {
                        prompt_identifier: promptId,
                        name: this.promptForm.value.title,
                        title: this.promptForm.value.title,
                        context: this.promptForm.value.context,
                        details: details,
                        prompt_set_id: this.data.prompt_set_id,
                        createdAt: createdAt,
                        createdBy: isEditMode ? (this.data.createdBy || "System") : "System",
                        isPrivate: isEditMode ? (this.data.isPrivate !== undefined ? this.data.isPrivate : true) : true,
                        examples: isEditMode ? this.data.examples || [] : [],
                        response_format: isEditMode ? this.data.response_format || "" : ""
                    };
                    this.dialogRef.close(result);
                },
                error: (error) => {
                    this.isSubmitting = false;
                    console.error('Error storing prompt:', error);
                    console.error('Error details:', error.error);
                }
            });
        }
    }

    onCancel() {
        this.dialogRef.close();
    }

    getParsedResult(resultString: string): any | null {
        try {
            // First try to parse as regular JSON
            let parsed = JSON.parse(resultString);
            if (parsed && (parsed.short_answer || parsed.summary || parsed.passage)) {
                return parsed;
            }
        } catch (error) {
            // If regular JSON parsing fails, try to handle Python-style dictionary format
            try {
                // Replace single quotes with double quotes for JSON compatibility
                // Handle the case where the string might contain single quotes within values
                const jsonString = resultString
                    .replace(/'/g, '"')  // Replace single quotes with double quotes
                    .replace(/\bTrue\b/g, 'true')  // Replace Python True with JSON true
                    .replace(/\bFalse\b/g, 'false')  // Replace Python False with JSON false
                    .replace(/\bNone\b/g, 'null');  // Replace Python None with JSON null
                
                const parsed = JSON.parse(jsonString);
                // Check if it has the expected structure for our JSON results
                if (parsed && (parsed.short_answer || parsed.summary || parsed.passage)) {
                    return parsed;
                }
            } catch (innerError) {
                // If both parsing attempts fail, return null
                return null;
            }
        }
        return null;
    }
}
